import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Heading, StatusDot } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { SignalsAPI, type DomainAccount, type Signal, type SignalStatus } from '@/api/signals'
import { MissionsAPI, type MissionProposal } from '@/api/missions'
import { DomainsAPI, domainSlug } from '@/api/domains'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { ProposalCard } from '@/components/ProposalCard'
import { useMissionSelection } from '@/store/missionSelection'
import { useEntityPeek } from '@/components/EntityPeek'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import './signals.css'

// Signals = interpreted change about the people, companies, and things you track,
// emptied like an inbox.
// fresh → seen → acted (a mission exists because of it) | dismissed (judged noise).

const IMP_COLOR: Record<string, string> = {
  High: 'var(--ae-color-feedback-error)',
  Medium: 'var(--ae-color-accent)',
  Low: 'var(--ae-color-text-muted)',
}

// Every lifecycle state, said in plain words — one source of truth for the chip
// label AND its hover gloss, so the page never leaks the raw machine word.
const STATE: Record<SignalStatus, { label: string; gloss: string }> = {
  fresh: { label: 'Waiting for you', gloss: 'New — nobody has looked at it yet. Waiting for your judgment.' },
  seen: { label: 'Seen', gloss: "You've looked, but haven't acted on it or set it aside yet." },
  acted: { label: 'You acted on it', gloss: 'You turned it into a mission — Diwa surfaces more like it.' },
  dismissed: { label: 'You set it aside', gloss: 'You judged it noise — Diwa holds similar ones back.' },
}

const TYPES = ['News', 'Role Change', 'Funding Event', 'Relationship Discovery', 'Reminder', 'Market Move']
const IMPORTANCES = ['High', 'Medium', 'Low']

const TYPE_CHIP: Record<string, string> = {
  News: 'dw-chip--info',
  'Role Change': 'dw-chip--warning',
  'Funding Event': 'dw-chip--success',
  'Relationship Discovery': 'dw-chip--info',
  Reminder: 'dw-chip--error',
  Podcast: 'dw-chip--accent',
  Market: 'dw-chip--success',
  'Market Move': 'dw-chip--success',
}
const CONF_CHIP: Record<string, string> = { High: 'dw-chip--success', Medium: 'dw-chip--warning', Low: '' }

const FILTERS: { key: string; label: string; admit: (s: Signal) => boolean }[] = [
  { key: 'inbox', label: 'Needs you', admit: (s) => s.status === 'fresh' || s.status === 'seen' },
  { key: 'acted', label: 'Acted on', admit: (s) => s.status === 'acted' },
  { key: 'dismissed', label: 'Set aside', admit: (s) => s.status === 'dismissed' },
  { key: 'all', label: 'All', admit: () => true },
]

const EMPTY_FORM = { title: '', type: 'News', importance: 'Medium', related: '', suggested_action: '', domain: '' }

/**
 * The account — the leg of the loop the operator could never see.
 *
 * Judgments were being recorded and read back only by the familiar, inside a mission you had
 * to summon by hand from a different page. So the app learned only when you remembered to ask
 * it to, and two weeks of setting things aside looked exactly like two weeks of nothing.
 *
 * Every number here is arithmetic over your own rows. A pattern is claimed only where the
 * backend's consistency floor is met, and where a replay engine exists the prompt routes into
 * the rule-smith so the numbers stay house-computed — never asserted by a model.
 */
function Account({
  account,
  loading,
  failed,
  api,
  label,
}: {
  account: DomainAccount[]
  loading: boolean
  failed: boolean
  api: SignalsAPI
  label: (slug: string) => string
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [summoned, setSummoned] = useState<Record<string, boolean>>({})
  const [reviewError, setReviewError] = useState<string | null>(null)

  // A domain nobody has judged in yet contributes nothing but noise to the record.
  const judged = account.filter((d) => d.acted + d.dismissed > 0)

  // "Not loaded" and "failed" must never wear the empty state's clothes: telling an
  // operator with a hundred rulings that they have judged nothing is the exact opposite
  // of what this panel is for, and it would read as the record having been lost.
  if (loading) {
    return <p className="dw-sig-learn__lede">Tallying your calls…</p>
  }
  if (failed) {
    return (
      <p className="dw-sig-learn__lede">
        Couldn't load your record just now — your judgments are safe, only this tally failed.
      </p>
    )
  }

  if (!judged.length) {
    return (
      <p className="dw-sig-learn__lede">
        Nothing judged yet — the first signal you act on or set aside starts this record, and
        it shows up here.
      </p>
    )
  }

  async function review(domain: string) {
    setBusy(domain)
    setReviewError(null)
    try {
      await api.reviewRules(domain)
      setSummoned((s) => ({ ...s, [domain]: true }))
    } catch {
      // Swallowing this left an expired session looking like a no-op button: the label
      // flipped back and nothing happened. Surface it instead.
      setReviewError('Could not summon the review. Try again.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="dw-sig-account">
      {reviewError && <div className="dw-error">{reviewError}</div>}
      {judged.map((d) => (
        <div key={d.domain} className="dw-sig-account__row">
          <div className="dw-sig-account__head">
            <b>{label(d.domain)}</b>{' '}
            <span className="dw-sig-account__since">
              {d.rules_updated_at
                ? `since you last changed these rules, ${new Date(
                    d.rules_updated_at,
                  ).toLocaleDateString()}`
                : 'all time — no rules written here yet'}
            </span>
          </div>
          <div className="dw-sig-account__tally">
            <span><b>{d.acted}</b> acted on</span>
            <span><b>{d.dismissed}</b> set aside</span>
            {d.waiting > 0 && <span className="dw-sig-account__waiting"><b>{d.waiting}</b> waiting</span>}
          </div>
          {d.patterns.map((p) => (
            <div key={p.type} className="dw-sig-account__pattern">
              <span>
                You've set aside <b>{p.dismissed} of {p.judged}</b> <b>{p.type}</b> signals here.
                {d.backtestable
                  ? ' Tala can replay a tighter rule against what you actually did.'
                  : ' Worth narrowing what counts as a signal in this domain.'}
              </span>
              {d.backtestable ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === d.domain || summoned[d.domain]}
                  onClick={() => review(d.domain)}
                >
                  {busy === d.domain
                    ? 'Summoning…'
                    : summoned[d.domain]
                    ? 'Review mission created ✦'
                    : '✦ Have Tala tighten this'}
                </Button>
              ) : (
                <Link className="dw-link" to="/pipelines">
                  Adjust the rules →
                </Link>
              )}
            </div>
          ))}
          {summoned[d.domain] && (
            <div className="dw-sig-account__pattern">
              <Link className="dw-link" to="/missions">
                open Missions →
              </Link>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export function Signals() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new SignalsAPI(diwaUrl), [diwaUrl])
  const missionsApi = useMemo(() => new MissionsAPI(diwaUrl), [diwaUrl])
  const domainsApi = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const graphApi = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const navigate = useNavigate()
  const selectMission = useMissionSelection((s) => s.select)
  const { openPeek } = useEntityPeek()

  const { data, isLoading, isError } = useQuery({ queryKey: ['signals'], queryFn: () => api.list() })
  const signals = data ?? []

  // The account: what these judgments have already added up to. Kept a separate query so a
  // slow or failed tally never blocks the feed itself — the inbox is the page's job.
  const {
    data: account,
    isLoading: accountLoading,
    isError: accountError,
  } = useQuery({ queryKey: ['signal-account'], queryFn: () => api.account() })

  // Resolve a signal's related names against the live constellation (same cache
  // the graph uses) so a subject entity opens the EntityPeek — a signal you can
  // walk to its star, not just read about.
  const { data: network } = useQuery({
    queryKey: ['graph-network', 'all'],
    queryFn: () => graphApi.network('all'),
  })
  const byName = useMemo(() => {
    const m = new Map<string, GraphNode>()
    for (const n of network?.nodes ?? []) m.set(n.name.toLowerCase(), n)
    return m
  }, [network])

  // A manual signal lands in one of YOUR regions — never a hardcoded 'crm'. Own
  // (unshared) domains only: you can't file a signal into someone else's world.
  const { data: domainsData } = useQuery({ queryKey: ['domains'], queryFn: () => domainsApi.list() })
  const myDomains = (domainsData?.domains ?? []).filter((d) => !d.shared)
  // Fall back to the backend's own default only when the user has no region at all.
  const defaultDomain = myDomains[0]?.slug ?? 'crm'

  const [filter, setFilter] = useState('inbox')
  const [showCreate, setShowCreate] = useState(false)
  const [showLearn, setShowLearn] = useState(true)
  // per-signal busy: drafting one signal's mission must not disable every card's buttons
  const [busyId, setBusyId] = useState<string | null>(null)
  const [formBusy, setFormBusy] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [proposing, setProposing] = useState<{ signal: Signal; proposal: MissionProposal } | null>(null)
  const [error, setError] = useState<string | null>(null)
  // per-signal track state — the "track" judgment is independent of the "act" one, so a
  // card can show both without either disabling the other.
  const [trackState, setTrackState] = useState<Record<string, 'idle' | 'busy' | 'done' | 'error'>>({})

  const admit = FILTERS.find((f) => f.key === filter)?.admit ?? FILTERS[0].admit
  const visible = signals.filter(admit)
  const inboxCount = signals.filter(FILTERS[0].admit).length

  // Every judgment moves the account too — refetch it alongside the feed so the record
  // visibly answers the call the operator just made. That immediacy IS the feature.
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['signals'] })
    await qc.invalidateQueries({ queryKey: ['signal-account'] })
  }

  async function setStatus(s: Signal, status: SignalStatus) {
    try {
      await api.updateStatus(s.id, status)
      await refresh()
    } catch {
      setError('That did not save. Try again.')
    }
  }

  async function toMission(s: Signal) {
    setBusyId(s.id)
    setError(null)
    try {
      const r = await api.proposeMission(s.id)
      setProposing({ signal: s, proposal: r.proposal })
    } catch {
      setError('The familiar could not draft a mission from that signal. Try again.')
    } finally {
      setBusyId(null)
    }
  }

  async function createFromProposal(p: MissionProposal) {
    if (!proposing) return
    const signalId = proposing.signal.id
    setBusyId(signalId)
    setError(null)
    let created
    try {
      created = await missionsApi.create({
        title: p.title.trim() || p.goal,
        goal: p.goal,
        status: 'Active',
        priority: 'High',
        domain: p.domain,
        target: p.target,
        protocol: p.protocol,
        steps: p.steps,
      })
    } catch {
      // the mission does NOT exist — retrying create is safe
      setError('Creating the mission failed. Try again.')
      setBusyId(null)
      return
    }
    // The mission exists from here on: never invite a retry of create. Marking the
    // signal acted is best-effort — a failed PATCH leaves it in the inbox, not a dupe.
    try {
      await api.updateStatus(signalId, 'acted')
    } catch {
      /* the signal stays fresh; dismiss it manually if the blip persists */
    }
    setProposing(null)
    setBusyId(null)
    await refresh()
    await qc.invalidateQueries({ queryKey: ['missions'] })
    selectMission(created.id)
    navigate(`/missions/${created.id}`)
  }

  async function create() {
    if (!form.title.trim() || formBusy) return
    setFormBusy(true)
    try {
      await api.create({
        title: form.title.trim(),
        type: form.type,
        importance: form.importance,
        confidence: 'Medium',
        related: form.related.split(',').map((s) => s.trim()).filter(Boolean),
        domain: form.domain || defaultDomain,
        suggested_action: form.suggested_action.trim(),
      })
      setForm(EMPTY_FORM)
      setShowCreate(false)
      await refresh()
    } finally {
      setFormBusy(false)
    }
  }

  async function remove(id: string) {
    try {
      await api.remove(id)
      await refresh()
    } catch {
      setError('Delete failed. Try again.')
    }
  }

  // The other half of the judgment: TRACK. Where "turn into a mission" acts on a signal,
  // tracking keeps its subject as a star in your constellation — the same "add to the
  // graph" path the Ledger's track uses. A subject already in the sky is already tracked;
  // otherwise it's placed into the signal's own domain, typed by that domain's first type.
  const domainOf = (slug: string) =>
    (domainsData?.domains ?? []).find((d) => d.slug === slug || d.slug === domainSlug(slug))

  async function trackAsStar(s: Signal) {
    const subject = (s.related[0] ?? '').trim() || s.title
    setError(null)
    setTrackState((st) => ({ ...st, [s.id]: 'busy' }))
    try {
      if (!byName.get(subject.toLowerCase())) {
        const dom = domainOf(s.domain)
        await graphApi.createEntity({
          type: dom?.entity_types?.[0] ?? 'Entity',
          name: subject,
          domain: dom?.slug ?? s.domain,
        })
        await qc.invalidateQueries({ queryKey: ['graph-network'] })
      }
      setTrackState((st) => ({ ...st, [s.id]: 'done' }))
    } catch {
      setTrackState((st) => ({ ...st, [s.id]: 'error' }))
    }
  }

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">Signals <HelpHint term="signal" /> <FlowDiagram /></div>
          <div className="dw-page-subtitle">
            What changed around the people, companies, and things you track — and the move it unlocks.
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Close' : '+ New signal'}
        </Button>
      </div>

      <div className="dw-chip-row" style={{ marginBottom: 14 }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`dw-chip dw-chip--toggle${filter === f.key ? ' dw-chip--on' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key === 'inbox' && inboxCount > 0 ? ` · ${inboxCount}` : ''}
          </button>
        ))}
      </div>

      <Card className="dw-sig-learn">
        <div className="dw-sig-learn__head">
          <Heading level={3}>How your calls shape what surfaces</Heading>
          <button
            type="button"
            className="dw-link"
            style={{ fontSize: 'var(--ae-font-size-label)' }}
            onClick={() => setShowLearn((v) => !v)}
            aria-expanded={showLearn}
          >
            {showLearn ? 'Hide' : 'Show'}
          </button>
        </div>
        {showLearn && (
          <>
            <p className="dw-sig-learn__lede">
              Everything here already matched what you told Diwa to watch for. What you do next
              teaches it what you actually care about:
            </p>
            <div className="dw-sig-teach">
              <div className="dw-sig-teach__row">
                <span className="dw-sig-teach__glyph dw-sig-teach__glyph--act" aria-hidden>✦</span>
                <span className="dw-sig-teach__body">
                  <b>Turn a signal into a mission</b> — Diwa learns to surface more like it.
                </span>
              </div>
              <div className="dw-sig-teach__row">
                <span className="dw-sig-teach__glyph dw-sig-teach__glyph--aside" aria-hidden>○</span>
                <span className="dw-sig-teach__body">
                  <b>Set one aside</b> — Diwa learns to hold similar ones back.
                </span>
              </div>
            </div>
            <ul className="dw-sig-states">
              <li><b>{STATE.fresh.label}</b> — new, nobody has looked yet.</li>
              <li><b>{STATE.seen.label}</b> — you've looked, but haven't decided.</li>
              <li><b>{STATE.acted.label}</b> — it became a mission.</li>
              <li><b>{STATE.dismissed.label}</b> — you judged it noise.</li>
            </ul>
            <Account
              account={account ?? []}
              loading={accountLoading}
              failed={accountError}
              api={api}
              label={(slug) => myDomains.find((d) => d.slug === slug)?.name ?? slug}
            />

            <div className="dw-sig-learn__lede" style={{ margin: 0 }}>
              Want to change what you hear about?{' '}
              <Link className="dw-link" to="/pipelines">
                Adjust what counts as a signal, per domain, in your Springs →
              </Link>
            </div>
          </>
        )}
      </Card>

      {proposing && (
        <Card style={{ marginBottom: 16 }}>
          <div className="dw-proposal-meta" style={{ marginBottom: 8 }}>
            from signal: {proposing.signal.title}
          </div>
          <ProposalCard
            key={proposing.signal.id} /* a different signal's proposal must not inherit stale edits */
            proposal={proposing.proposal}
            busy={busyId === proposing.signal.id}
            onCreate={createFromProposal}
            onDiscard={() => setProposing(null)}
          />
        </Card>
      )}

      {showCreate && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <input className="dw-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="dw-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="dw-input" value={form.importance} onChange={(e) => setForm({ ...form, importance: e.target.value })}>
                {IMPORTANCES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {myDomains.length > 0 && (
                <select
                  className="dw-input"
                  value={form.domain || defaultDomain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                  title="Which of your domains this signal belongs to"
                >
                  {myDomains.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
                </select>
              )}
            </div>
            <input className="dw-input" placeholder="Related entities (comma-separated)" value={form.related} onChange={(e) => setForm({ ...form, related: e.target.value })} />
            <input className="dw-input" placeholder="Suggested action" value={form.suggested_action} onChange={(e) => setForm({ ...form, suggested_action: e.target.value })} />
            <div>
              <Button variant="primary" size="sm" disabled={formBusy} onClick={create}>Create signal</Button>
            </div>
          </div>
        </Card>
      )}

      {isLoading && <div className="dw-empty">Reading your signals…</div>}
      {isError && <div className="dw-error">Signals didn't load — check the connection and retry.</div>}
      {!isLoading && !isError && visible.length === 0 && !showCreate && (
        <div className="dw-empty">
          {filter === 'inbox'
            ? 'Nothing waiting — new signals land here as the people and companies you track make news.'
            : 'Nothing here.'}
        </div>
      )}

      {error && <div className="dw-notice dw-run-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {visible.map((s) => (
          <Card
            key={s.id}
            className="dw-accent"
            style={{
              ['--dw-accent' as string]: IMP_COLOR[s.importance] ?? 'var(--ae-color-text-muted)',
              opacity: s.status === 'dismissed' ? 0.55 : 1,
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', cursor: s.status === 'fresh' ? 'pointer' : 'default' }}
              onClick={() => s.status === 'fresh' && setStatus(s, 'seen')}
              title={s.status === 'fresh' ? 'Mark seen' : undefined}
            >
              <div style={{ fontWeight: s.status === 'fresh' ? 700 : 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                {/* A fresh signal is the live one demanding judgment — green pulse.
                    TODO: once springs expose per-signal freshness/health, drive this
                    dot from that (via livenessState) rather than the fresh flag alone. */}
                {s.status === 'fresh' && (
                  <HelpHint gloss={STATE.fresh.gloss}>
                    <StatusDot state="live" size="sm" label={STATE.fresh.gloss} />
                  </HelpHint>
                )}
                {s.title}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))', textTransform: 'uppercase', letterSpacing: '0.12em', color: IMP_COLOR[s.importance] ?? 'var(--ae-color-text-muted)', whiteSpace: 'nowrap' }}>
                  {s.importance}
                </span>
                {/* Lake-synced signals (source_url) get no hard-delete: the row IS the dedup
                    key, and deleting it resurrects the signal fresh on the next hourly sync.
                    Dismiss is the durable judgment for those. */}
                {!s.source_url && (
                  <button className="dw-action-btn" style={{ padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); remove(s.id) }}>×</button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
              <span className={`dw-chip ${TYPE_CHIP[s.type] ?? ''}`}>{s.type}</span>
              <span className={`dw-chip ${CONF_CHIP[s.confidence] ?? ''}`}>confidence · {s.confidence}</span>
              {s.status !== 'fresh' && (
                <HelpHint gloss={STATE[s.status].gloss}>
                  <span className="dw-chip dw-sig-state-chip">{STATE[s.status].label}</span>
                </HelpHint>
              )}
              {/* A related name that lives in your constellation becomes a quick-look:
                  click opens the EntityPeek anchored here, with a route into the graph.
                  stopPropagation so a fresh card isn't marked seen by the same click. */}
              {s.related.map((r) => {
                const node = byName.get(r.toLowerCase())
                if (node) {
                  return (
                    <button
                      key={r}
                      type="button"
                      className="dw-cite"
                      title={`Quick look at ${node.name}`}
                      onClick={(e) => { e.stopPropagation(); openPeek(node, e.currentTarget) }}
                    >
                      {r}
                    </button>
                  )
                }
                return <span key={r} className="dw-chip" style={{ opacity: 0.7 }}>{r}</span>
              })}
            </div>
            {s.suggested_action && (
              <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-accent)', marginBottom: 8 }}>→ {s.suggested_action}</div>
            )}
            {/* Provenance, in plain words: this signal exists because your domain's rules
                caught it. Name the rules (a link into that domain when you own it) and keep
                the trail home to the source page — the same fact, from pool row to signal. */}
            {(() => {
              const dom = domainOf(s.domain)
              const owned = !!dom && !dom.shared
              const domName = dom?.name ?? s.domain
              return (
                <div className="dw-sig-prov">
                  crossed your{' '}
                  {owned ? (
                    <Link
                      className="dw-link"
                      to={`/domains/${dom!.slug}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {domName} rules
                    </Link>
                  ) : (
                    <span className="dw-sig-prov__dom">{domName} rules</span>
                  )}
                  {s.source_url && (
                    <>
                      {' · '}
                      <a
                        className="dw-link"
                        href={s.source_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        view source ↗
                      </a>
                    </>
                  )}
                </div>
              )
            })()}
            {s.status !== 'acted' && s.status !== 'dismissed' && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* Two judgments, side by side — TRACK (→ your constellation) and ACT
                    (→ a mission). A fresh signal offers both; setting aside is the third. */}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={trackState[s.id] === 'busy' || trackState[s.id] === 'done'}
                  title={trackState[s.id] === 'done'
                    ? 'Tracked — kept as a star in your constellation'
                    : 'Track this signal’s subject — keep it as a star you hold'}
                  onClick={() => trackAsStar(s)}
                >
                  {trackState[s.id] === 'busy'
                    ? '☆ Tracking…'
                    : trackState[s.id] === 'done'
                    ? '✦ Tracked'
                    : trackState[s.id] === 'error'
                    ? '☆ Retry track'
                    : '☆ Track as a star'}
                </Button>
                <Button size="sm" variant="primary" disabled={busyId === s.id} onClick={() => toMission(s)}>
                  {busyId === s.id ? '✦ Drafting…' : '✦ Turn into a mission'}
                </Button>
                <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => setStatus(s, 'dismissed')}>
                  Set aside
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}
